/**
 * Second tool: Price levels (Y) vs Gamma Exposure and Options Inventory (X).
 * Uses current mode data (RTL, Legacy RTL, Stable). When a metric is absent, shows "Missing data".
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ScannerData } from '../../types/ScannerData';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useMmecFx } from '../../theme/mmecFx';
import { useChartTheme } from '../../theme/chartTheme';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import ToolState from '../mmec/ToolState';
import './VerticalSpotGammaVsOIChart.ds.css';
import { useDataSource } from '../../contexts/DataSourceContext';
import { usePersistentFilters } from '../../utils/persistentFilters';
import { getFilterOptions, getUnderlyingSymbol, sortByStrikePrice } from '../../utils/filterUtils';
import { expiryRowMatchesSelection } from '../../utils/expiryFilterUtils';
import { getCBOEFieldValue } from '../../utils/dataModeUtils';
import { computeSpotGammaGexMme, getUnderlyingPriceFallback, spotGammaToDisplayIndex } from '../../utils/gammaCalculations';
import { dataService } from '../../services/dataService';
import { readStablePrimarySessionFirst, readStableUnderlyingsSessionFirst } from '../../utils/stableTabStorage';
import { DataMode } from '../../types/DataMode';
import { useHistoricalModal } from '../../contexts/HistoricalModalContext';
import { ProToolFilterSlot, useFilterRailSlotSurface } from '../mmec/ProToolFilterSlot';
import {
  buildStrikeExpiryStackRows,
  expiryLabelToStackBase,
  expiryPaletteColors,
  selectedStackExpiries,
  shortenExpiryLegend,
} from '../../utils/expiryStackChartData';

const VERTICAL_SIDE_BY_SIDE_STORAGE_KEY = 'VerticalSpotGammaVsOIChart-side-by-side-calls-puts';
const VERTICAL_STACK_EACH_SIDE_STORAGE_KEY = 'VerticalSpotGammaVsOIChart-stack-each-side';
const VERTICAL_STACK_ALL_SIDES_STORAGE_KEY = 'VerticalSpotGammaVsOIChart-stack-all-sides';
const VERTICAL_SHOW_VOL_GEX_STORAGE_KEY = 'VerticalSpotGammaVsOIChart-show-volume-gex';
const VERTICAL_MERGE_VOL_GEX_OI_STORAGE_KEY = 'VerticalSpotGammaVsOIChart-merge-vol-gex-oi';

const CALL_SIDE_PALETTE = [
  '#22c55e', // green
  '#3b82f6', // blue
  '#a16207', // brown/amber
  '#111827', // near black
  '#06b6d4', // cyan
  '#0f766e', // teal
  '#f59e0b', // amber
  '#4b5563', // slate
];

const PUT_SIDE_PALETTE = [
  '#ef4444', // red
  '#a855f7', // purple
  '#ec4899', // pink
  '#7c3aed', // violet
  '#be123c', // rose
  '#db2777', // fuchsia
  '#9333ea', // deep purple
  '#dc2626', // dark red
];

function sideStackColor(index: number, total: number, isCall: boolean, isDark: boolean): string {
  const palette = isCall ? CALL_SIDE_PALETTE : PUT_SIDE_PALETTE;
  const base = palette[index % palette.length];
  if (!isDark) return base;
  const darkPalette = isCall
    ? ['#4ade80', '#60a5fa', '#ca8a04', '#e5e7eb', '#22d3ee', '#14b8a6', '#fbbf24', '#9ca3af']
    : ['#f87171', '#c084fc', '#f472b6', '#a78bfa', '#fb7185', '#e879f9', '#d8b4fe', '#ef4444'];
  return darkPalette[index % darkPalette.length];
}

function num(item: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = item[k];
    if (v === null || v === undefined || v === '' || v === '-') continue;
    const x = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (Number.isFinite(x)) return x;
  }
  return 0;
}

function side(item: Record<string, unknown>): 'C' | 'P' {
  const r = String(item['Option Type'] ?? item.optionType ?? '').toUpperCase();
  return r === 'C' || r === 'CALL' ? 'C' : 'P';
}

export interface VerticalSpotGammaVsOIChartProps {
  data: ScannerData[];
  dataMode?: DataMode;
  hideFilters?: boolean;
}

export const VerticalSpotGammaVsOIChart: React.FC<VerticalSpotGammaVsOIChartProps> = ({
  data: dataProp,
  dataMode = 'accumulated',
  hideFilters = false
}) => {
  const { language } = useLanguage();
  const { colors, isDarkMode } = useTheme();
  const fx = useMmecFx();
  const chartT = useChartTheme();
  const { dataSource } = useDataSource();
  const { openHistorical } = useHistoricalModal();
  const {
    selectedUnderlying,
    selectedExpiry: expiry,
    selectedExpiries,
    selectedSide: sideFilter,
    setSelectedUnderlying,
    setSelectedExpiry: setExpiry,
    setSelectedExpiries,
    setSelectedSide: setSideFilter,
    setFilterState,
  } = usePersistentFilters('VerticalSpotGammaVsOIChart', dataMode === '5min' ? '5min' : 'accumulated');

  const [cboeData, setCboeData] = useState<ScannerData[]>([]);
  const [underlyings, setUnderlyings] = useState<string[]>(() => {
    const list = readStableUnderlyingsSessionFirst();
    return list.length > 0 ? list : ['SPY'];
  });

  const [fetchKey, setFetchKey] = useState(0);
  const [sideBySideCallsPuts, setSideBySideCallsPuts] = useState<boolean>(() => {
    try {
      return typeof localStorage !== 'undefined' &&
        localStorage.getItem(VERTICAL_SIDE_BY_SIDE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [stackEachSide, setStackEachSide] = useState<boolean>(() => {
    try {
      const v = typeof localStorage !== 'undefined'
        ? localStorage.getItem(VERTICAL_STACK_EACH_SIDE_STORAGE_KEY)
        : null;
      return v == null ? true : v === '1';
    } catch {
      return true;
    }
  });
  const [stackAllSides, setStackAllSides] = useState<boolean>(() => {
    try {
      return typeof localStorage !== 'undefined' &&
        localStorage.getItem(VERTICAL_STACK_ALL_SIDES_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [showVolumeGex, setShowVolumeGex] = useState<boolean>(() => {
    try {
      return typeof localStorage !== 'undefined' &&
        localStorage.getItem(VERTICAL_SHOW_VOL_GEX_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mergeVolGexWithOI, setMergeVolGexWithOI] = useState<boolean>(() => {
    try {
      return typeof localStorage !== 'undefined' &&
        localStorage.getItem(VERTICAL_MERGE_VOL_GEX_OI_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const hasLoadedOnceRef = React.useRef(false);

  const useTableData = dataSource === 'rtl_legacy';
  const chartData = useMemo(() => {
    if (useTableData) return (dataProp ?? []).filter(Boolean);
    if (dataSource === 'stable' && dataProp?.length) return dataProp;
    return cboeData ?? [];
  }, [useTableData, dataSource, dataProp, cboeData]);

  useEffect(() => {
    if (dataSource !== 'stable') {
      setLoading(false);
      setLoadFailed(false);
      return;
    }
    if (dataProp?.length) {
      setLoading(false);
      setLoadFailed(false);
      return;
    }
    let cancelled = false;
    if (!hasLoadedOnceRef.current) setLoading(true);
    setLoadFailed(false);
    const load = async () => {
      try {
        const symbols = underlyings.length > 0 ? underlyings : ['SPY'];
        const expiryRange = localStorage.getItem('mmec_expiry_range') || 'weekly';
        const data = await dataService.fetchCboeGreeksData(symbols, { expiryRange });
        if (!cancelled) {
          setCboeData(data ?? []);
          setLoadFailed(!data?.length);
          hasLoadedOnceRef.current = true;
        }
      } catch (_) {
        if (!cancelled) {
          setCboeData([]);
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dataSource, dataProp?.length, underlyings.join(','), fetchKey]);

  useEffect(() => {
    if (dataSource !== 'stable') return;
    const id = setInterval(() => setFetchKey((k) => k + 1), 30000);
    return () => clearInterval(id);
  }, [dataSource]);

  useEffect(() => {
    const onRefreshed = () => setFetchKey((k) => k + 1);
    window.addEventListener('stableDataFetched', onRefreshed);
    return () => window.removeEventListener('stableDataFetched', onRefreshed);
  }, []);

  useEffect(() => {
    const handler = () => {
      const sym = readStablePrimarySessionFirst();
      if (sym) setUnderlyings([sym]);
      setFetchKey((k) => k + 1);
    };
    const strikeExpiryHandler = () => setFetchKey((k) => k + 1);
    window.addEventListener('stableDataFetchRequested', handler);
    window.addEventListener('stableStrikeOrExpiryChanged', strikeExpiryHandler);
    return () => {
      window.removeEventListener('stableDataFetchRequested', handler);
      window.removeEventListener('stableStrikeOrExpiryChanged', strikeExpiryHandler);
    };
  }, []);

  const filterOpts = useMemo(() => getFilterOptions(chartData), [chartData]);
  const expiries = filterOpts.availableExpiries ?? [];
  const fromDataUnderlyings = filterOpts.availableUnderlyings ?? [];
  const availableUnderlyings = fromDataUnderlyings.length > 0 ? fromDataUnderlyings : underlyings;
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const primaryExpiryForModals = useMemo(() => {
    if (!selectedExpiries?.length || selectedExpiries.includes('all')) return undefined;
    const digits = (s: string) => String(s).replace(/\D/g, '').slice(0, 8);
    const sorted = [...selectedExpiries].filter((e) => e !== 'all').sort((a, b) => digits(a).localeCompare(digits(b)));
    return sorted[0];
  }, [selectedExpiries]);

  const prevExpiriesKeyRef = useRef('');
  useEffect(() => {
    const key = expiries.slice().sort().join('\0');
    if (prevExpiriesKeyRef.current === key) return;
    prevExpiriesKeyRef.current = key;
    if (!expiries.length) return;
    if (!selectedExpiries.includes('all')) {
      const bad = selectedExpiries.filter((e) => !expiries.includes(e));
      if (bad.length) {
        setFilterState((prev) => {
          const kept = selectedExpiries.filter((e) => expiries.includes(e));
          const next = kept.length ? kept : ['all'];
          const digits = (s: string) => String(s).replace(/\D/g, '').slice(0, 8);
          const sorted = next.filter((x) => x !== 'all').sort((a, b) => digits(a).localeCompare(digits(b)));
          const finalList = sorted.length ? sorted : ['all'];
          const primary = finalList[0] === 'all' ? 'all' : finalList[0];
          return { ...prev, selectedExpiries: finalList, selectedExpiry: primary };
        });
      }
    } else if (expiry !== 'all') {
      setExpiry('all');
    }
  }, [expiries, selectedExpiries, expiry, setFilterState, setExpiry]);

  const filtered = useMemo(() => {
    if (!chartData?.length) return [];
    return chartData.filter((row) => {
      const underlying = getUnderlyingSymbol(row);
      const matchU = selectedUnderlying === 'all' || !selectedUnderlying || underlying === selectedUnderlying;
      const matchE = expiryRowMatchesSelection(row['Expiry Date'], selectedExpiries);
      const s = side(row as unknown as Record<string, unknown>);
      const matchS = sideFilter === 'all' || (sideFilter === 'C' && s === 'C') || (sideFilter === 'P' && s === 'P');
      return matchU && matchE && matchS;
    });
  }, [chartData, selectedUnderlying, selectedExpiries, sideFilter]);

  const underlyingPriceFallback = useMemo(() => getUnderlyingPriceFallback(filtered, dataMode), [filtered, dataMode]);

  const byStrike = useMemo(() => {
    const map = new Map<number, {
      strike: number;
      openInterest: number;
      spotGamma: number;
      gammaExposure: number;
      volumeGammaExposure: number;
      callOI: number;
      putOI: number;
      callSpotGamma: number;
      putSpotGamma: number;
      putOINeg: number;
    }>();
    for (const row of filtered) {
      const r = row as unknown as Record<string, unknown>;
      const st = num(r, 'Strike', 'Strike Price') || 0;
      if (!st) continue;
      const oi = Number(getCBOEFieldValue(row, 'Open Interest')) || 0;
      const { spotGamma: spotGammaValue, gex: gexValue, volumeGex: volumeGexValue } = computeSpotGammaGexMme(row, dataMode, underlyingPriceFallback);
      const isCall = side(r) === 'C';

      const cur = map.get(st);
      if (cur) {
        cur.openInterest += oi;
        cur.spotGamma += spotGammaValue;
        cur.gammaExposure += gexValue;
        cur.volumeGammaExposure += volumeGexValue;
        if (isCall) {
          cur.callOI += oi;
          cur.callSpotGamma += spotGammaValue;
        } else {
          cur.putOI += oi;
          cur.putSpotGamma += spotGammaValue;
        }
      } else {
        map.set(st, {
          strike: st,
          openInterest: oi,
          spotGamma: spotGammaValue,
          gammaExposure: gexValue,
          volumeGammaExposure: volumeGexValue,
          callOI: isCall ? oi : 0,
          putOI: isCall ? 0 : oi,
          callSpotGamma: isCall ? spotGammaValue : 0,
          putSpotGamma: isCall ? 0 : spotGammaValue,
          putOINeg: 0
        });
      }
    }
    const S = underlyingPriceFallback > 0 ? underlyingPriceFallback : 100;
    const list = Array.from(map.values()).map((x) => ({
      ...x,
      strike: Number(x.strike),
      putOINeg: -(x.putOI || 0),
      spotGammaDisplay: spotGammaToDisplayIndex(x.spotGamma, S)
    }));
    return sortByStrikePrice(list);
  }, [filtered, dataMode, underlyingPriceFallback]);

  const stackExpiryList = useMemo(() => selectedStackExpiries(selectedExpiries), [selectedExpiries]);
  const stackPalette = useMemo(
    () => expiryPaletteColors(stackExpiryList?.length ?? 0, isDarkMode),
    [stackExpiryList, isDarkMode],
  );
  const stackBases = useMemo(() => (stackExpiryList ?? []).map(expiryLabelToStackBase), [stackExpiryList]);
  const byStrikeStacked = useMemo(() => {
    if (!stackExpiryList?.length) return null;
    return buildStrikeExpiryStackRows(filtered, stackExpiryList, dataMode, underlyingPriceFallback, sideFilter);
  }, [filtered, stackExpiryList, dataMode, underlyingPriceFallback, sideFilter]);
  const chartRows = stackExpiryList && byStrikeStacked?.length ? byStrikeStacked : byStrike;
  const useExpiryStackCharts = Boolean(stackExpiryList?.length && byStrikeStacked?.length);

  // Add mergedVolumeGex field when merge toggle is on (volume gamma + OI gamma combined)
  const chartRowsWithMerge = useMemo(() => {
    if (!mergeVolGexWithOI) return chartRows;
    return chartRows.map((row) => ({
      ...row,
      mergedVolumeGex: ((row as unknown as { volumeGammaExposure?: number }).volumeGammaExposure ?? 0) + ((row as unknown as { gammaExposure?: number }).gammaExposure ?? 0),
    }));
  }, [chartRows, mergeVolGexWithOI]);

  const hasGammaExposureData = useMemo(() => {
    if (useExpiryStackCharts && byStrikeStacked?.length && stackBases.length) {
      return byStrikeStacked.some((row) =>
        stackBases.some((b) => Math.abs(Number(row[`gex_${b}`] ?? 0)) > 1e-9),
      );
    }
    return byStrike.some((b) => (b.gammaExposure ?? 0) !== 0);
  }, [useExpiryStackCharts, byStrikeStacked, stackBases, byStrike]);

  const hasVolumeGexData = useMemo(() => {
    if (useExpiryStackCharts && byStrikeStacked?.length && stackBases.length) {
      return byStrikeStacked.some((row) =>
        stackBases.some((b) => Math.abs(Number(row[`volGex_${b}`] ?? 0)) > 1e-9),
      );
    }
    return byStrike.some((b) => (b.volumeGammaExposure ?? 0) !== 0);
  }, [useExpiryStackCharts, byStrikeStacked, stackBases, byStrike]);

  const hasSpotGammaData = useMemo(() => {
    if (useExpiryStackCharts && byStrikeStacked?.length && stackBases.length) {
      return byStrikeStacked.some((row) =>
        stackBases.some((b) => Math.abs(Number(row[`sgDisp_${b}`] ?? 0)) > 1e-9),
      );
    }
    return byStrike.some((b) => (b.spotGamma ?? 0) !== 0);
  }, [useExpiryStackCharts, byStrikeStacked, stackBases, byStrike]);

  const hasOIData = useMemo(() => {
    if (useExpiryStackCharts && byStrikeStacked?.length && stackBases.length) {
      return byStrikeStacked.some((row) =>
        stackBases.some((b) =>
          Math.abs(Number(row[`callOI_${b}`] ?? 0)) > 1e-9 || Math.abs(Number(row[`putOI_${b}`] ?? 0)) > 1e-9,
        ),
      );
    }
    return byStrike.some((b) => (b.openInterest ?? 0) !== 0 || (b.callOI ?? 0) !== 0 || (b.putOI ?? 0) !== 0);
  }, [useExpiryStackCharts, byStrikeStacked, stackBases, byStrike]);
  const missingDataLabel = language === 'ar' ? 'بيانات مفقودة' : 'Missing data';

  const underlyingPrice = useMemo(() => {
    if (!filtered.length) return null;
    const v = getCBOEFieldValue(filtered[0], 'Underlying Price');
    return v > 0 ? v : null;
  }, [filtered]);

  const symbol = useMemo(() => {
    if (!filtered.length) return '';
    const s = getUnderlyingSymbol(filtered[0]);
    return s ? `$${s}` : '';
  }, [filtered]);

  const effectiveUnderlying = (selectedUnderlying && selectedUnderlying !== 'all') ? selectedUnderlying : (symbol ? symbol.replace(/^\$/, '') : 'SPY');
  const effectiveExpiry = primaryExpiryForModals ?? (expiries[0] || '');
  const handleStrikeClick = (payload: { strike?: number }, optionType: 'CALL' | 'PUT' = 'CALL') => {
    if (payload?.strike == null) return;
    openHistorical({ underlying: effectiveUnderlying, strike: payload.strike, expiry: effectiveExpiry, optionType, source: 'spot_gamma' });
  };
  const onBarClick = (data: unknown, optionType: 'CALL' | 'PUT' = 'CALL') => {
    const payload = (data as { payload?: { strike?: number } })?.payload ?? (data as { strike?: number });
    handleStrikeClick(payload, optionType);
  };

  const strokeClr = chartT.text;
  const gridClr = chartT.grid;
  /** Design System preview: direction = up/down, spot gamma / GEX = --series-gex, spot = --price-line dashed (Recharts needs hex). */
  const dsOn = isDesignSystemOn();
  const dsSuccess = dsOn ? '#26a69a' : colors.chartSuccess;
  const dsError = dsOn ? '#ef5350' : colors.chartError;
  const dsPrimary = dsOn ? '#7986cb' : colors.chartPrimary;
  const dsSpotLine = dsOn ? '#b2b5be' : colors.chartWarning;
  const dsSpotDash = dsOn ? '4 4' : '3 4';
  const tooltipContentStyle: React.CSSProperties = {
    background: 'var(--elev)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 12,
    fontFamily: 'var(--font-ui)',
    boxShadow: 'var(--lift-2)',
    color: 'var(--txt)',
  };
  const tooltipLabelStyle: React.CSSProperties = { color: 'var(--txt)', fontWeight: 500 };
  const tooltipItemStyle: React.CSSProperties = { color: 'var(--txt-2)' };
  const legendStyle: React.CSSProperties = { fontSize: 11, color: 'var(--muted)' };
  const rootStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 12,
    border: `1px solid ${colors.surfaceBorder}`,
    backgroundColor: colors.surface,
    color: colors.text,
    minHeight: 'calc(100vh - 80px)',
  };
  const panelStyle: React.CSSProperties = {
    background: 'var(--elev)',
    border: '1px solid var(--hair)',
    borderRadius: 10,
    padding: 12,
  };
  const headerDivider: React.CSSProperties = { borderBottom: `1px solid ${colors.surfaceBorder}` };
  const MIN_PX_PER_STRIKE = 16;
  const viewportH = typeof window !== 'undefined' ? window.innerHeight - 240 : 700;
  const chartHeight = Math.max(600, viewportH, chartRows.length * MIN_PX_PER_STRIKE);
  const yTickInterval = chartRows.length > 80 ? Math.ceil(chartRows.length / 50) - 1 : 0;

  const stackExpiryTooltip = useCallback(
    ({
      active,
      payload,
      label,
    }: {
      active?: boolean;
      payload?: Array<{ dataKey?: string; value?: unknown; color?: string; name?: string }>;
      label?: string | number;
    }) => {
      if (!active || !payload?.length) return null;
      const rows = payload.filter((p) => p.value != null && Math.abs(Number(p.value)) > 1e-9);
      if (!rows.length) return null;
      return (
        <div style={{ ...tooltipContentStyle, padding: '8px 10px', borderRadius: 8 }} className="text-xs shadow-lg">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {language === 'ar' ? `سعر التنفيذ ${label}` : `Strike ${label}`}
          </div>
          {rows.map((p) => (
            <div key={String(p.dataKey)} style={{ color: p.color ?? colors.text, marginBottom: 2 }}>
              {p.name}: {Number(p.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
          ))}
        </div>
      );
    },
    [colors.text, language, tooltipContentStyle],
  );

  useEffect(() => {
    try {
      localStorage.setItem(VERTICAL_SIDE_BY_SIDE_STORAGE_KEY, sideBySideCallsPuts ? '1' : '0');
    } catch {
      // ignore persistence failures
    }
  }, [sideBySideCallsPuts]);

  useEffect(() => {
    try {
      localStorage.setItem(VERTICAL_STACK_EACH_SIDE_STORAGE_KEY, stackEachSide ? '1' : '0');
      localStorage.setItem(VERTICAL_STACK_ALL_SIDES_STORAGE_KEY, stackAllSides ? '1' : '0');
    } catch {
      // ignore persistence failures
    }
  }, [stackEachSide, stackAllSides]);

  useEffect(() => {
    try {
      localStorage.setItem(VERTICAL_SHOW_VOL_GEX_STORAGE_KEY, showVolumeGex ? '1' : '0');
    } catch {
      // ignore persistence failures
    }
  }, [showVolumeGex]);

  useEffect(() => {
    try {
      localStorage.setItem(VERTICAL_MERGE_VOL_GEX_OI_STORAGE_KEY, mergeVolGexWithOI ? '1' : '0');
    } catch {
      // ignore persistence failures
    }
  }, [mergeVolGexWithOI]);

  const VerticalSpotFiltersBody = () => {
    const inRail = useFilterRailSlotSurface();
    const expiryScrollStyle: React.CSSProperties = inRail
      ? {
          ...fx.selectTiny,
          width: '100%',
          padding: '8px 10px',
          fontSize: 12,
          borderRadius: 8,
          maxHeight: 'min(52vh, 380px)',
          minHeight: 120,
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          color: colors.text,
        }
      : {
          ...fx.selectTiny,
          width: '100%',
          padding: '8px 10px',
          fontSize: 12,
          maxHeight: 'min(40vh, 320px)',
          minHeight: 140,
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          color: colors.text,
        };

    return (
      <section
        className={
          inRail
            ? 'shrink-0 flex flex-col gap-0 p-0 w-full min-w-0'
            : 'shrink-0 p-3 w-full min-w-0 flex flex-col gap-0'
        }
        style={
          inRail
            ? { fontFamily: 'var(--font-ui, "IBM Plex Sans", sans-serif)', paddingBottom: 4 }
            : headerDivider
        }
      >
        <p
          className={inRail ? 'mb-2 font-semibold uppercase tracking-wide px-1' : 'text-xs mb-2'}
          style={
            inRail
              ? { fontSize: 11, letterSpacing: '0.06em', color: colors.textMuted }
              : { color: colors.textSecondary }
          }
        >
          {language === 'ar' ? 'الأصل والنوع' : 'Underlying & side'}
        </p>
        <div
          className={
            inRail
              ? 'grid grid-cols-1 gap-3 px-1'
              : 'grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl'
          }
        >
          <div className="min-w-0 w-full">
            <label
              className={inRail ? 'block mb-1 font-semibold uppercase tracking-wide' : 'block text-sm font-medium mb-1'}
              style={
                inRail
                  ? { fontSize: 11, letterSpacing: '0.06em', color: colors.textMuted }
                  : { color: colors.textSecondary }
              }
            >
              {language === 'ar' ? 'الأصل' : 'Underlying'}
            </label>
            <select
              value={selectedUnderlying}
              onChange={(e) => setSelectedUnderlying(e.target.value)}
              className="w-full"
              style={
                inRail
                  ? { ...fx.selectTiny, width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8 }
                  : { ...fx.selectTiny, width: '100%', padding: '6px 8px' }
              }
            >
              <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
              {availableUnderlyings.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div className="min-w-0 w-full">
            <label
              className={inRail ? 'block mb-1 font-semibold uppercase tracking-wide' : 'block text-sm font-medium mb-1'}
              style={
                inRail
                  ? { fontSize: 11, letterSpacing: '0.06em', color: colors.textMuted }
                  : { color: colors.textSecondary }
              }
            >
              {language === 'ar' ? 'نوع' : 'Side'}
            </label>
            <select
              value={sideFilter}
              onChange={(e) => setSideFilter(e.target.value)}
              className="w-full"
              style={
                inRail
                  ? { ...fx.selectTiny, width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8 }
                  : { ...fx.selectTiny, width: '100%', padding: '6px 8px' }
              }
            >
              <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
              <option value="C">Call</option>
              <option value="P">Put</option>
            </select>
          </div>
        </div>

        <div
          className={`mt-4 pt-4 w-full min-w-0 flex flex-col flex-1 min-h-0 ${inRail ? 'px-1' : ''}`}
          style={{ borderTop: `1px solid ${colors.surfaceBorder}` }}
        >
          <label
            className={inRail ? 'block mb-1 font-semibold uppercase tracking-wide' : 'block text-sm font-medium mb-1'}
            style={
              inRail
                ? { fontSize: 11, letterSpacing: '0.06em', color: colors.textMuted }
                : { color: colors.textSecondary }
            }
          >
            {language === 'ar' ? 'تواريخ الانتهاء (متعددة)' : 'Expiry (multi-select)'}
          </label>
          <div className="w-full flex-1 min-h-0" style={expiryScrollStyle}>
            <label className="flex items-center gap-2 cursor-pointer mb-1.5" style={{ color: colors.text }}>
              <input
                type="checkbox"
                checked={selectedExpiries.includes('all')}
                onChange={() => setSelectedExpiries(['all'])}
              />
              <span>{language === 'ar' ? 'كل التواريخ' : 'All expiries'}</span>
            </label>
            {expiries.map((exp) => (
              <label key={exp} className="flex items-center gap-2 cursor-pointer mb-1" style={{ color: colors.text, fontSize: 11 }}>
                <input
                  type="checkbox"
                  checked={!selectedExpiries.includes('all') && selectedExpiries.includes(exp)}
                  onChange={() => {
                    if (selectedExpiries.includes('all')) {
                      setSelectedExpiries([exp]);
                      return;
                    }
                    const has = selectedExpiries.includes(exp);
                    const next = has ? selectedExpiries.filter((e) => e !== exp) : [...selectedExpiries, exp];
                    setSelectedExpiries(next.length ? next : ['all']);
                  }}
                />
                {stackExpiryList?.includes(exp) ? (
                  <span
                    className="rounded-full shrink-0"
                    style={{
                      width: 8,
                      height: 8,
                      backgroundColor: stackPalette[Math.max(0, stackExpiryList.indexOf(exp))],
                      flexShrink: 0,
                    }}
                    title={language === 'ar' ? 'لون هذا التاريخ على الرسم البياني' : 'Chart color for this expiry'}
                    aria-hidden
                  />
                ) : null}
                <span className="break-all">{exp}</span>
              </label>
            ))}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="vsgoi-ds-root flex flex-col min-h-0 rounded-lg" style={rootStyle}>
      <header className="vsgoi-ds-header shrink-0 px-4 py-2" style={headerDivider}>
        <h2 className="vsgoi-ds-title text-lg font-semibold">
          {language === 'ar' ? 'مستويات السعر مقابل الفتح المفتوح' : 'Price Levels vs Open Interest'}
        </h2>
        {symbol && <p className="vsgoi-ds-sym text-sm mt-0.5" style={{ color: colors.textMuted }}>{symbol}</p>}
        <label
          className={`mt-2 inline-flex items-center gap-2 text-xs ${useExpiryStackCharts ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
          title={
            language === 'ar'
              ? 'في وضع تواريخ متعددة: عرض Call وPut جنبًا إلى جنب بدل الاتجاه المعاكس.'
              : 'In multi-expiry mode: show Call and Put side-by-side instead of mirrored.'
          }
        >
          <input
            type="checkbox"
            className="rounded"
            checked={sideBySideCallsPuts}
            disabled={!useExpiryStackCharts || stackAllSides}
            onChange={(e) => setSideBySideCallsPuts(e.target.checked)}
          />
          <span>{language === 'ar' ? 'Call/Put جنبًا إلى جنب' : 'Side-by-side Calls/Puts'}</span>
        </label>
        <label
          className={`mt-2 ml-4 inline-flex items-center gap-2 text-xs ${useExpiryStackCharts ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
          title={language === 'ar' ? 'تجميع كل تواريخ كل جانب في عمود واحد لكل جانب.' : 'Stack each side into one bar per side (calls vs puts).'}
        >
          <input
            type="checkbox"
            className="rounded"
            checked={stackEachSide}
            disabled={!useExpiryStackCharts || stackAllSides}
            onChange={(e) => setStackEachSide(e.target.checked)}
          />
          <span>{language === 'ar' ? 'تكديس كل جانب' : 'Stack each side'}</span>
        </label>
        <label
          className={`mt-2 ml-4 inline-flex items-center gap-2 text-xs ${useExpiryStackCharts ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
          title={language === 'ar' ? 'تكديس كل Call وPut معًا في عمود واحد لكل سعر.' : 'Stack calls and puts together into one bar per strike.'}
        >
          <input
            type="checkbox"
            className="rounded"
            checked={stackAllSides}
            disabled={!useExpiryStackCharts}
            onChange={(e) => setStackAllSides(e.target.checked)}
          />
          <span>{language === 'ar' ? 'تكديس الكل في عمود واحد' : 'Stack all into one bar'}</span>
        </label>
        <label
          className="mt-2 ml-4 inline-flex items-center gap-2 text-xs cursor-pointer"
          title={
            language === 'ar'
              ? 'عرض تعرض جاما على الحجم (يستخدم الحجم بدل الفتح المفتوح)'
              : 'Show volume gamma exposure (uses volume instead of open interest)'
          }
        >
          <input
            type="checkbox"
            className="rounded"
            checked={showVolumeGex}
            onChange={(e) => setShowVolumeGex(e.target.checked)}
          />
          <span>{language === 'ar' ? 'تعرض جاما على الحجم' : 'Volume gamma exposure'}</span>
        </label>
        {showVolumeGex && (
          <label
            className="mt-2 ml-4 inline-flex items-center gap-2 text-xs cursor-pointer"
            title={
              language === 'ar'
                ? 'دمج تعرض جاما على الحجم مع تعرض جاما العادي (على الفتح المفتوح)'
                : 'Merge volume gamma exposure with OI-based gamma exposure'
            }
          >
            <input
              type="checkbox"
              className="rounded"
              checked={mergeVolGexWithOI}
              onChange={(e) => setMergeVolGexWithOI(e.target.checked)}
            />
            <span>{language === 'ar' ? 'دمج مع الفتح المفتوح' : 'Merge with OI'}</span>
          </label>
        )}
      </header>

      {!hideFilters && (
        <ProToolFilterSlot>
          <VerticalSpotFiltersBody />
        </ProToolFilterSlot>
      )}

      <section className="flex-1 min-h-0 p-3 overflow-auto">
        {dsOn && (loading || (loadFailed && !chartData?.length) || !chartRows.length) ? (
          loading ? (
            <ToolState kind="loading" lang={language === 'ar' ? 'ar' : 'en'} fill rows={6} />
          ) : loadFailed && !chartData?.length ? (
            <ToolState kind="error" lang={language === 'ar' ? 'ar' : 'en'} fill body={language === 'ar' ? 'فشل تحميل البيانات. تحقق من الاتصال بالخادم.' : 'Failed to load data. Check backend connection.'} />
          ) : (
            <ToolState kind="empty" lang={language === 'ar' ? 'ar' : 'en'} fill title={language === 'ar' ? 'لا توجد بيانات للعرض' : 'No data to display'} />
          )
        ) : loading ? (
          <p className="py-8 text-center" style={{ color: 'var(--muted)', fontSize: 13 }}>{language === 'ar' ? 'جاري تحميل البيانات (قد يستغرق بضع ثوانٍ)...' : 'Loading data (may take a few seconds)…'}</p>
        ) : loadFailed && !chartData?.length ? (
          <p className="py-8 text-center" style={{ color: 'var(--muted)', fontSize: 13 }}>{language === 'ar' ? 'فشل تحميل البيانات. تحقق من الاتصال بالخادم.' : 'Failed to load data. Check backend connection.'}</p>
        ) : !chartRows.length ? (
          <p className="py-8 text-center" style={{ color: 'var(--muted)', fontSize: 13 }}>{language === 'ar' ? 'لا توجد بيانات للعرض' : 'No data to display'}</p>
        ) : (
          <div className={`grid grid-cols-1 gap-4 flex-1 min-h-0 ${showVolumeGex ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`} style={{ minHeight: chartHeight }}>
            {/* Left: Gamma exposure – price (Y) vs shares per $ move (X) */}
            <div className="vsgoi-ds-panel rounded p-3 flex flex-col min-h-0" style={panelStyle}>
              <h3 className="vsgoi-ds-panel-title text-sm font-semibold mb-2 lowercase first-letter:uppercase">
                {language === 'ar' ? 'تعرض جاما' : 'gamma exposure'}
              </h3>
              {useExpiryStackCharts && (
                <p className="text-xs mb-1" style={{ color: colors.textMuted }}>
                  {language === 'ar' ? 'ألوان حسب تاريخ الانتهاء — الشرائح المكدسة تعرض مساهمة كل تاريخ.' : 'Colors = expiry; stacked segments per strike.'}
                </p>
              )}
              {!hasGammaExposureData ? (
                <p className="py-6 text-center text-sm opacity-80" style={{ color: colors.textSecondary }}>{missingDataLabel}</p>
              ) : (
                <>
                  <div className="flex-1 min-h-0" style={{ minHeight: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRows as Record<string, unknown>[]} layout="vertical" margin={{ top: 8, right: 16, left: 56, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 4" stroke={gridClr} horizontal={true} vertical={true} />
                        {!sideBySideCallsPuts && !stackAllSides && <ReferenceLine x={0} stroke={chartT.border} strokeWidth={1} />}
                        {underlyingPrice != null && (
                          <ReferenceLine y={underlyingPrice} stroke={dsSpotLine} strokeDasharray={dsSpotDash} strokeWidth={1} />
                        )}
                        <XAxis type="number" stroke={strokeClr} tick={{ fill: strokeClr, fontSize: 11, fontFamily: 'var(--font-mono)' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${Number(v) >= 1000 ? (Number(v) / 1000).toFixed(1) + 'K' : v}`} domain={['dataMin', 'dataMax']} />
                        <YAxis type="number" dataKey="strike" stroke={strokeClr} tick={{ fill: strokeClr, fontSize: 11, fontFamily: 'var(--font-mono)' }} tickLine={false} axisLine={false} tickFormatter={(v) => Number(v).toFixed(0)} width={56} domain={['dataMin', 'dataMax']} interval={yTickInterval} />
                        <Tooltip content={useExpiryStackCharts ? stackExpiryTooltip : undefined} contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={useExpiryStackCharts ? undefined : ((v: number) => [Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), language === 'ar' ? 'تعرض جاما' : 'Gamma exposure'])} labelFormatter={(l) => `${language === 'ar' ? 'سعر' : 'Price'}: ${l}`} />
                        <Legend wrapperStyle={legendStyle} />
                        {useExpiryStackCharts && stackExpiryList ? (
                          stackExpiryList.map((exp, i) => (
                            <Bar
                              key={exp}
                              stackId="vgex"
                              dataKey={`gex_${expiryLabelToStackBase(exp)}`}
                              name={shortenExpiryLegend(exp)}
                              fill={stackPalette[i]}
                              radius={0}
                              onClick={(data: unknown) => onBarClick(data, 'CALL')}
                              cursor="pointer"
                            />
                          ))
                        ) : (
                          <Bar dataKey="gammaExposure" name={language === 'ar' ? 'تعرض جاما' : 'gamma exposure'} fill={dsPrimary} radius={0} onClick={(data: unknown) => onBarClick(data, 'CALL')} cursor="pointer">
                            {(chartRows as typeof byStrike).map((entry, index) => (
                              <Cell key={index} fill={(entry.gammaExposure ?? 0) >= 0 ? dsSuccess : dsError} />
                            ))}
                          </Bar>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{language === 'ar' ? 'أسهم لكل حركة 1$' : 'shares per $ move'}</p>
                </>
              )}
            </div>

            {/* Middle: Spot Gamma – price (Y) vs dollar gamma per 1% (X), matches competitor by-strike values */}
            <div className="vsgoi-ds-panel rounded p-3 flex flex-col min-h-0" style={panelStyle}>
              <h3 className="vsgoi-ds-panel-title text-sm font-semibold mb-2 lowercase first-letter:uppercase">
                {language === 'ar' ? 'سخيار بيع جاما' : 'spot gamma'}
              </h3>
              {!hasSpotGammaData ? (
                <p className="py-6 text-center text-sm opacity-80" style={{ color: colors.textSecondary }}>{missingDataLabel}</p>
              ) : (
                <>
                  <div className="flex-1 min-h-0" style={{ minHeight: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRows as Record<string, unknown>[]} layout="vertical" margin={{ top: 8, right: 16, left: 56, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 4" stroke={gridClr} horizontal={true} vertical={true} />
                        <ReferenceLine x={0} stroke={chartT.border} strokeWidth={1} />
                        {underlyingPrice != null && (
                          <ReferenceLine y={underlyingPrice} stroke={dsSpotLine} strokeDasharray={dsSpotDash} strokeWidth={1} />
                        )}
                        <XAxis type="number" stroke={strokeClr} tick={{ fill: strokeClr, fontSize: 11, fontFamily: 'var(--font-mono)' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${Number(v) >= 1000 ? (Number(v) / 1000).toFixed(1) + 'K' : Number(v).toFixed(1)}`} domain={['dataMin', 'dataMax']} />
                        <YAxis type="number" dataKey="strike" stroke={strokeClr} tick={{ fill: strokeClr, fontSize: 11, fontFamily: 'var(--font-mono)' }} tickLine={false} axisLine={false} tickFormatter={(v) => Number(v).toFixed(0)} width={56} domain={['dataMin', 'dataMax']} interval={yTickInterval} />
                        <Tooltip content={useExpiryStackCharts ? stackExpiryTooltip : undefined} contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={useExpiryStackCharts ? undefined : ((v: number) => [Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), language === 'ar' ? 'سخيار بيع جاما (مؤشر)' : 'Spot gamma (index)'])} labelFormatter={(l) => `${language === 'ar' ? 'سعر' : 'Price'}: ${l}`} />
                        <Legend wrapperStyle={legendStyle} />
                        {useExpiryStackCharts && stackExpiryList ? (
                          stackExpiryList.map((exp, i) => (
                            <Bar
                              key={exp}
                              stackId="vsg"
                              dataKey={`sgDisp_${expiryLabelToStackBase(exp)}`}
                              name={shortenExpiryLegend(exp)}
                              fill={stackPalette[i]}
                              radius={0}
                              onClick={(data: unknown) => onBarClick(data, 'CALL')}
                              cursor="pointer"
                            />
                          ))
                        ) : (
                          <Bar dataKey="spotGammaDisplay" name={language === 'ar' ? 'سخيار بيع جاما' : 'spot gamma'} radius={0} onClick={(data: unknown) => onBarClick(data, 'CALL')} cursor="pointer">
                            {(chartRows as typeof byStrike).map((entry, index) => (
                              <Cell key={index} fill={((entry as { spotGammaDisplay?: number }).spotGammaDisplay ?? entry.spotGamma ?? 0) >= 0 ? dsSuccess : dsError} />
                            ))}
                          </Bar>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{language === 'ar' ? 'دولار جاما لكل 1%' : 'dollar gamma per 1%'}</p>
                </>
              )}
            </div>

            {/* Right: Options inventory – price (Y) vs contract count (X), call (green) / put (red) */}
            <div className="vsgoi-ds-panel rounded p-3 flex flex-col min-h-0" style={panelStyle}>
              <h3 className="vsgoi-ds-panel-title text-sm font-semibold mb-2 lowercase first-letter:uppercase">
                {language === 'ar' ? 'مخزون الخيارات' : 'options inventory'}
              </h3>
              {!hasOIData ? (
                <p className="py-6 text-center text-sm opacity-80" style={{ color: colors.textSecondary }}>{missingDataLabel}</p>
              ) : (
                <>
                  <div className="flex-1 min-h-0" style={{ minHeight: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRows as Record<string, unknown>[]} layout="vertical" margin={{ top: 8, right: 16, left: 56, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 4" stroke={gridClr} horizontal={true} vertical={true} />
                        <ReferenceLine x={0} stroke={chartT.border} strokeWidth={1} />
                        {underlyingPrice != null && (
                          <ReferenceLine y={underlyingPrice} stroke={dsSpotLine} strokeDasharray={dsSpotDash} strokeWidth={1} />
                        )}
                        <XAxis type="number" stroke={strokeClr} tick={{ fill: strokeClr, fontSize: 11, fontFamily: 'var(--font-mono)' }} tickLine={false} axisLine={false} tickFormatter={(v) => String(v)} domain={['dataMin', 'dataMax']} />
                        <YAxis type="number" dataKey="strike" stroke={strokeClr} tick={{ fill: strokeClr, fontSize: 11, fontFamily: 'var(--font-mono)' }} tickLine={false} axisLine={false} tickFormatter={(v) => Number(v).toFixed(0)} width={56} domain={['dataMin', 'dataMax']} interval={yTickInterval} />
                        <Tooltip content={useExpiryStackCharts ? stackExpiryTooltip : undefined} contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={useExpiryStackCharts ? undefined : ((v: number) => [Number(v).toLocaleString(), ''])} labelFormatter={(l) => `${language === 'ar' ? 'سعر' : 'Price'}: ${l}`} />
                        <Legend wrapperStyle={legendStyle} />
                        {useExpiryStackCharts && stackExpiryList ? (
                          stackAllSides ? (
                            stackExpiryList.map((exp, i) => (
                              <Bar
                                key={`all-${exp}`}
                                stackId="vall"
                                dataKey={`oi_${expiryLabelToStackBase(exp)}`}
                                name={language === 'ar' ? `إجمالي ${shortenExpiryLegend(exp)}` : `Total ${shortenExpiryLegend(exp)}`}
                                fill={stackPalette[i]}
                                radius={0}
                                onClick={(data: unknown) => onBarClick(data, 'CALL')}
                                cursor="pointer"
                              />
                            ))
                          ) : (
                            <>
                              {stackExpiryList.map((exp, i) => (
                                <Bar
                                  key={`call-${exp}`}
                                  stackId={stackEachSide ? 'vcall' : undefined}
                                  dataKey={`callOI_${expiryLabelToStackBase(exp)}`}
                                  name={language === 'ar' ? `كال ${shortenExpiryLegend(exp)}` : `Call ${shortenExpiryLegend(exp)}`}
                                  fill={sideStackColor(i, stackExpiryList.length, true, isDarkMode)}
                                  radius={0}
                                  onClick={(data: unknown) => onBarClick(data, 'CALL')}
                                  cursor="pointer"
                                />
                              ))}
                              {stackExpiryList.map((exp, i) => (
                                <Bar
                                  key={`put-${exp}`}
                                  stackId={stackEachSide ? 'vput' : undefined}
                                  dataKey={`${sideBySideCallsPuts ? 'putOI' : 'putOINeg'}_${expiryLabelToStackBase(exp)}`}
                                  name={language === 'ar' ? `خيار بيع ${shortenExpiryLegend(exp)}` : `Put ${shortenExpiryLegend(exp)}`}
                                  fill={sideStackColor(i, stackExpiryList.length, false, isDarkMode)}
                                  radius={0}
                                  onClick={(data: unknown) => onBarClick(data, 'PUT')}
                                  cursor="pointer"
                                />
                              ))}
                            </>
                          )
                        ) : (
                          <>
                            <Bar dataKey="callOI" name="CALLS" fill={dsSuccess} radius={0} onClick={(data: unknown) => onBarClick(data, 'CALL')} cursor="pointer" />
                            <Bar dataKey="putOINeg" name="PUTS" fill={dsError} radius={0} onClick={(data: unknown) => onBarClick(data, 'PUT')} cursor="pointer" />
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{language === 'ar' ? 'عدد العقود (أقرب انتهاء)' : 'contract count (nearest expiry)'}</p>
                </>
              )}
            </div>

            {/* Fourth column: Volume Gamma Exposure – price (Y) vs volume-based gamma exposure (X) */}
            {showVolumeGex && (
              <div className="vsgoi-ds-panel rounded p-3 flex flex-col min-h-0" style={panelStyle}>
                <h3 className="vsgoi-ds-panel-title text-sm font-semibold mb-2 lowercase first-letter:uppercase">
                  {language === 'ar'
                    ? 'تعرض جاما على الحجم'
                    : 'volume gamma exposure'}
                  {mergeVolGexWithOI &&
                    ` (${language === 'ar' ? 'مدمج' : 'merged'})`}
                </h3>
                {useExpiryStackCharts && (
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>
                    {language === 'ar' ? 'ألوان حسب تاريخ الانتهاء — الشرائح المكدسة تعرض مساهمة كل تاريخ.' : 'Colors = expiry; stacked segments per strike.'}
                  </p>
                )}
                {!hasVolumeGexData && !mergeVolGexWithOI ? (
                  <p className="py-6 text-center text-sm opacity-80" style={{ color: colors.textSecondary }}>{missingDataLabel}</p>
                ) : (
                  <>
                    <div className="flex-1 min-h-0" style={{ minHeight: chartHeight }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartRowsWithMerge as Record<string, unknown>[]} layout="vertical" margin={{ top: 8, right: 16, left: 56, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 4" stroke={gridClr} horizontal={true} vertical={true} />
                          {!sideBySideCallsPuts && !stackAllSides && <ReferenceLine x={0} stroke={chartT.border} strokeWidth={1} />}
                          {underlyingPrice != null && (
                            <ReferenceLine y={underlyingPrice} stroke={dsSpotLine} strokeDasharray={dsSpotDash} strokeWidth={1} />
                          )}
                          <XAxis type="number" stroke={strokeClr} tick={{ fill: strokeClr, fontSize: 11, fontFamily: 'var(--font-mono)' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${Number(v) >= 1000 ? (Number(v) / 1000).toFixed(1) + 'K' : v}`} domain={['dataMin', 'dataMax']} />
                          <YAxis type="number" dataKey="strike" stroke={strokeClr} tick={{ fill: strokeClr, fontSize: 11, fontFamily: 'var(--font-mono)' }} tickLine={false} axisLine={false} tickFormatter={(v) => Number(v).toFixed(0)} width={56} domain={['dataMin', 'dataMax']} interval={yTickInterval} />
                          <Tooltip content={useExpiryStackCharts ? stackExpiryTooltip : undefined} contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={useExpiryStackCharts ? undefined : ((v: number) => [Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), language === 'ar' ? 'تعرض جاما على الحجم' : 'Volume gamma exposure'])} labelFormatter={(l) => `${language === 'ar' ? 'سعر' : 'Price'}: ${l}`} />
                          <Legend wrapperStyle={legendStyle} />
                          {useExpiryStackCharts && stackExpiryList ? (
                            stackExpiryList.map((exp, i) => (
                              <Bar
                                key={exp}
                                stackId="vvolgex"
                                dataKey={`volGex_${expiryLabelToStackBase(exp)}`}
                                name={shortenExpiryLegend(exp)}
                                fill={stackPalette[i]}
                                radius={0}
                                onClick={(data: unknown) => onBarClick(data, 'CALL')}
                                cursor="pointer"
                              />
                            ))
                          ) : (
                            <Bar
                              dataKey={mergeVolGexWithOI ? 'mergedVolumeGex' : 'volumeGammaExposure'}
                              name={language === 'ar' ? 'تعرض جاما على الحجم' : 'volume gamma exposure'}
                              fill={dsPrimary}
                              radius={0}
                              onClick={(data: unknown) => onBarClick(data, 'CALL')}
                              cursor="pointer"
                            >
                              {(chartRowsWithMerge as typeof byStrike).map((entry, index) => {
                                const val = mergeVolGexWithOI
                                  ? (entry as unknown as { mergedVolumeGex?: number }).mergedVolumeGex
                                  : entry.volumeGammaExposure;
                                return (
                                  <Cell key={index} fill={(val ?? 0) >= 0 ? dsSuccess : dsError} />
                                );
                              })}
                            </Bar>
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{language === 'ar' ? 'أسهم لكل حركة 1$ (على الحجم)' : 'shares per $ move (volume-based)'}</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default VerticalSpotGammaVsOIChart;
